import { v4 as uuidv4 } from 'uuid'
import {
  CookieName,
  getCookie,
  removeCookie,
  resolveCookieSameSite,
  SameSiteAttribute,
  setCookie
} from '@src/helpers/cookie'
import { IUnauthenticatedRealmSettings } from '@src/types/realmSettings.types'
import { IApiResponseError } from '@src/types/errors.types'
import _some from 'lodash/some'
import _get from 'lodash/get'
import usersAPI from '@src/api/user'
import { AuthErrorCode, OAuthClientId } from '@src/types/auth.types'
import { generatePkceParams, consumePkceParams } from '@src/utilities/pkce'
import { FeatureToggleNames } from '@src/composables/featureToggles/featureToggle.names'
import { isWebview } from '@dvlden/is-webview'
import { realmSettingsApi } from '@src/api/apiModuleInstances'
import appWrapperHook from './appWrapper'
import {
  getWindowLocationObject, isInCrossDomainIframe
} from './iframeDetection'

let redirectInProgress = false
let pkceAuthEnabled = false

export function setPkceAuthEnabled(value: boolean) {
  pkceAuthEnabled = value
}

export function cleanUpAuthCookies(sameSite: SameSiteAttribute): void {
  removeCookie(CookieName.API_TOKEN, sameSite)
  removeCookie(CookieName.AUTH_TRACKING, sameSite)
  removeCookie(CookieName.ID_TOKEN, sameSite)
  removeCookie(CookieName.SM_LOGOUT_REDIRECT, sameSite)
}

/**
 * The callback we expected after the client returns from a successful auth flow with keycloak.
 * @return true if callback to /oauth/idp-callback otherwise false
 */
function isIdpCallback() {
  return window.location.href.includes('/oauth/idp-callback')
}

/**
 * Checks if the current window is inside an iframe or a web view.
 *
 * @returns {boolean} Returns true if the current window is inside an iframe or a web view, otherwise false.
 */
function isIframeOrWebview(): boolean {
  return (isInCrossDomainIframe() || isWebview(window.navigator.userAgent))
}

function getInfiniteLoopProtectionCounter() {
  return Number(getCookie(CookieName.INFINITE_LOOP_PROTECTION)) ?? 0
}

function incrementInfiniteLoopProtectionCounter(sameSite: SameSiteAttribute) {
  const counter = getInfiniteLoopProtectionCounter()
  const cookieValue = (counter + 1).toString()
  setCookie({
    name: CookieName.INFINITE_LOOP_PROTECTION,
    value: cookieValue,
    expiration: new Date(Date.now() + 7 * 1000),
    sameSite
  })
}

export function resetInfiniteLoopProtection(sameSite: SameSiteAttribute = 'Strict') {
  removeCookie(CookieName.INFINITE_LOOP_PROTECTION, sameSite)
}

export function resetRedirectInProgress() {
  redirectInProgress = false
}

export function iframeSafeRedirection(url: string) {
  /*
  Checks if the parent frame is also the Pegasus application and if it is the case,
  redirect on the parent window location.

  If there is no parent, or Starmind is embedded in a customer's website as iframe
  (if the origins don't match), then redirect on the current location.
  */
  const location = getWindowLocationObject()
  location.assign(url)
}

function getErrorCodeUrlParams(code?: string) {
  const params: { code?: string; iframe?: string } = {}
  if (code) {
    params.code = code
  }
  if (isInCrossDomainIframe()) {
    params.iframe = 'true'
  }
  return new URLSearchParams(params).toString()
}

export function isBehindAuthentication(url: string): boolean {
  return !_some(
    [ '/logout', '/invite', '/external-app', '/error', '/oauth/idp-callback' ],
    (publicPath) => url.includes(publicPath)
  )
}

export async function logout(): Promise<void> {
  appWrapperHook('logout')

  const { data } = await realmSettingsApi.cached.get()
  const sameSite = resolveCookieSameSite(data.auth.cookie_same_site_none)
  const logoutUrl = getLogoutUrl(data)
  cleanUpAuthCookies(sameSite)
  iframeSafeRedirection(logoutUrl)
}

export function isAuthenticated(): boolean {
  return !!getCookie(CookieName.API_TOKEN)
}

export async function login(errorResponseData?: IApiResponseError) {
  if (redirectInProgress) return
  redirectInProgress = true
  const targetUrl = (getWindowLocationObject().href).replace(window.location.origin, '')
  if (isBehindAuthentication(targetUrl)) {
    const { data } = await realmSettingsApi.cached.get()

    const sameSite = resolveCookieSameSite(data.auth.cookie_same_site_none)
    cleanUpAuthCookies(sameSite)
    const errorCode = errorResponseData?.error?.code

    if (errorCode === 'UserNotYetAvailable') {
      iframeSafeRedirection(`/error/401/?${getErrorCodeUrlParams(errorCode)}`)
    } else if (getInfiniteLoopProtectionCounter() >= 3) {
      /*
      * If the user has already been redirected to the auth provider within the last 7 seconds
      * show an error page instead, to prevent an infinite loop.
      * This can happen if the user successfully logs in at the auth provider but an API endpoint requiring authentication rejects the token.
      */
      iframeSafeRedirection(`/error/401/?${getErrorCodeUrlParams(errorCode)}`)
    } else {
      incrementInfiniteLoopProtectionCounter(sameSite)
      if (targetUrl.length > 1 && !targetUrl.includes('angular/')) { // never put /angular/ into the redirect cookie
        setCookie({
          name: CookieName.SM_REDIRECT,
          value: encodeURIComponent(targetUrl),
          sameSite
        })
      }
      const loginUrl = await getLoginUrl(data)

      iframeSafeRedirection(loginUrl)
    }
  }
}

export async function handle401(errorResponseData: IApiResponseError): Promise<void> {
  appWrapperHook('unauthenticatedRequest')
  await login(errorResponseData)
}

export async function initAuth(cookieSameSiteNone: boolean) {
  const sameSite = resolveCookieSameSite(cookieSameSiteNone)
  try {
    await saveTokensFromIdpCallback(sameSite)
    redirectIdpCallbackToTarget()
  } catch (error) {
    iframeSafeRedirection(`/error/401?${getErrorCodeUrlParams(AuthErrorCode.UnableToStoreApiToken)}`)
  }

  if (!isBehindAuthentication(window.location.pathname)) {
    removeCookie(CookieName.SM_REDIRECT, sameSite)
    return
  }
  /**
   * If authenticated (apiToken cookie is present) then fetch the user's roles in order to have them
   * ready before the application bootstrap. The roles are then available synchronously at any time.
   */
  if (isAuthenticated()) {
    try {
      await usersAPI.cached.getRoles()
      removeCookie(CookieName.SM_REDIRECT, sameSite)
    } catch (error) {
      const responseStatus = _get(error, 'response.status')
      if (responseStatus === 503) {
        iframeSafeRedirection('/error/503')
      } else {
        // by default go to login
        await handle401(error as IApiResponseError)
      }
    }
  } else {
    await login({
      error: {
        code: 'NoApiTokenCookiePresent',
        message: 'API token is missing in cookie.'
      }
    })
  }
}

/**
 * Tries to resolve the initial target url after the idp callback and then trigger the client redirect.
 */
export function redirectIdpCallbackToTarget() {
  if (isIdpCallback()) {
    // read the possible redirect from the cookie
    const redirectFromCookie = getCookie(CookieName.SM_REDIRECT)?.replace(/angular\//, '')

    // read query params
    const redirectQueryParam = new URL(
      window.location.toString()
    ).searchParams.get('redirect')

    if (redirectFromCookie) {
      window.history.replaceState({}, '', redirectFromCookie)
    } else if (redirectQueryParam) {
      window.history.replaceState({}, '', redirectQueryParam)
    } else {
      window.history.replaceState({}, '', '/')
    }
  }
}

/**
 * Exchanges the PKCE authorization code from the IDP callback for tokens and persists them as cookies.
 * @param sameSite the required SameSite cookie attribute for the auth cookies
 */
export async function saveTokensFromIdpCallback(sameSite = 'Strict' as SameSiteAttribute): Promise<void> {
  if (!isIdpCallback()) return

  const { searchParams } = new URL(window.location.toString())
  const idpError = searchParams.get('error')

  if (idpError) {
    iframeSafeRedirection(`/error/401/?${getErrorCodeUrlParams(AuthErrorCode.IdpError)}`)
    return
  }

  const pkceParams = consumePkceParams()

  if (!pkceParams) {
    const hashFragmentParams = window.location.hash
      .split('&')
      .reduce((paramsObject: Record<string, string>, keyValueString) => {
        const modifiedKeyValueString = keyValueString.replace('#', '')
        const [ key, value ] = modifiedKeyValueString.split('=')
        // eslint-disable-next-line no-param-reassign
        paramsObject[decodeURIComponent(key)] = decodeURIComponent(value)
        return paramsObject
      }, {})

    if (hashFragmentParams.access_token) {
      setCookie({ name: CookieName.API_TOKEN, value: hashFragmentParams.access_token, sameSite })
      if (hashFragmentParams.id_token) {
        setCookie({ name: CookieName.ID_TOKEN, value: hashFragmentParams.id_token, sameSite })
      }
      if (isIframeOrWebview()) {
        if (hashFragmentParams.logout_redirect_uri) {
          setCookie({ name: CookieName.SM_LOGOUT_REDIRECT, value: hashFragmentParams.logout_redirect_uri, sameSite })
        }
        if (hashFragmentParams.client_login_uri) {
          setCookie({ name: CookieName.SM_CLIENT_LOGIN, value: hashFragmentParams.client_login_uri, sameSite })
        }
      }
    } else {
      iframeSafeRedirection(`/error/401/?${getErrorCodeUrlParams('NoAccessTokenFragmentPresent')}`)
    }
    return
  }

  const code = searchParams.get('code')
  const returnedState = searchParams.get('state')

  if (!code) {
    iframeSafeRedirection(`/error/401/?${getErrorCodeUrlParams(AuthErrorCode.NoAuthorizationCodePresent)}`)
    return
  }

  if (pkceParams.state !== returnedState) {
    iframeSafeRedirection(`/error/401/?${getErrorCodeUrlParams(AuthErrorCode.PkceStateMismatch)}`)
    return
  }

  const redirectUri = `${window.location.origin}/oauth/idp-callback/`

  const response = await fetch('/oauth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      code_verifier: pkceParams.verifier,
      client_id: pkceParams.clientId,
      redirect_uri: redirectUri
    })
  })

  if (!response.ok) {
    const errBody = await response.text().catch(() => '(unreadable)')
    // eslint-disable-next-line no-console
    console.error('[PKCE] Token exchange failed', response.status, errBody)
    throw new Error(`Token exchange failed: ${response.status} ${errBody}`)
  }

  const tokens = await response.json() as { access_token?: string; id_token?: string }

  if (!tokens.access_token) {
    throw new Error('Token exchange response missing access_token')
  }

  setCookie({ name: CookieName.API_TOKEN, value: tokens.access_token, sameSite })
  if (tokens.id_token) {
    setCookie({ name: CookieName.ID_TOKEN, value: tokens.id_token, sameSite })
  }
}

async function fetchPkceToggleEnabled(): Promise<boolean> {
  if (!window.UNLEASH_ENABLED || !window.UNLEASH_ENDPOINT || !window.UNLEASH_TOKEN) {
    return pkceAuthEnabled
  }
  try {
    const url = new URL(window.UNLEASH_ENDPOINT)
    url.searchParams.set('appName', 'Pegasus')
    const response = await fetch(url.toString(), {
      headers: { Authorization: window.UNLEASH_TOKEN }
    })
    if (!response.ok) return pkceAuthEnabled
    const { toggles } = await response.json() as { toggles: Array<{ name: string; enabled: boolean }> }
    return toggles.find((t) => t.name === FeatureToggleNames.RELEASE_NOJIRA_PKCE_AUTH)?.enabled ?? false
  } catch {
    return pkceAuthEnabled
  }
}

export async function getLoginUrl(realmSettings: IUnauthenticatedRealmSettings): Promise<string> {
  const redirectUri = encodeURIComponent(`${window.location.origin}/oauth/idp-callback/`)
  const scopes = encodeURIComponent('accounts-api openid')
  const frontendIframeClientId = OAuthClientId.QA_FRONTEND_IFRAME
  let clientId: OAuthClientId | string = OAuthClientId.QA_FRONTEND
  if (isIframeOrWebview()) {
    const clientLoginUrlFromParam = getCookie(CookieName.SM_CLIENT_LOGIN)
    if (clientLoginUrlFromParam) {
      return clientLoginUrlFromParam
    }
    const urlParams = new URL(window.location.toString())
    const clientIdParam = urlParams.searchParams.get('client_id')
    clientId = clientIdParam || frontendIframeClientId
  }
  const pkceEnabled = await fetchPkceToggleEnabled()
  if (!pkceEnabled) {
    const responseTypes = encodeURIComponent('token id_token')
    const nonce = uuidv4()
    return `${realmSettings?.auth.keycloak_url}/realms/${realmSettings?.metadata.realm_id}/protocol/openid-connect/auth?response_mode=fragment&response_type=${responseTypes}&scope=${scopes}&client_id=${clientId}&redirect_uri=${redirectUri}&nonce=${nonce}`
  }
  const { codeChallenge, state } = await generatePkceParams(clientId)
  return `${realmSettings?.auth.keycloak_url}/realms/${realmSettings?.metadata.realm_id}/protocol/openid-connect/auth?response_type=code&scope=${scopes}&client_id=${clientId}&redirect_uri=${redirectUri}&code_challenge=${codeChallenge}&code_challenge_method=S256&state=${state}`
}

export function getLogoutUrl(realmSettings: IUnauthenticatedRealmSettings) {
  const idToken = getCookie(CookieName.ID_TOKEN)
  // default pegasus logout page
  let logoutRedirectUri = encodeURIComponent(`${window.location.origin}/logout`)
  if (isIframeOrWebview()) {
    // try to resolve client specific logout page in case it was provided in the authenticated iframe or webview context
    const logoutRedirectFromParam = getCookie(CookieName.SM_LOGOUT_REDIRECT)
    if (logoutRedirectFromParam) {
      logoutRedirectUri = encodeURIComponent(logoutRedirectFromParam)
    }
  }
  return `${realmSettings?.auth.keycloak_url}/realms/${realmSettings?.metadata.realm_id}/protocol/openid-connect/logout?post_logout_redirect_uri=${logoutRedirectUri}&id_token_hint=${idToken}`
}
